import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useState } from "react";

import { parseFieldSchema } from "@/components/event-records/event-record-schema";
import { deriveTimingRows } from "@/components/event-records/fire-drill";
import config from "@/config/client";
import type { EventRecordDetailResponseDto } from "@/generated/model";
import { formatFieldValue } from "@/lib/event-records/format-field-value";
import { eventRecordPrintStatus } from "@/lib/event-records/print-status";

/**
 * Public print-ready page for event record PDFs
 * print convention in print/incidents.$incidentId.tsx):
 *  - Lives OUTSIDE `_authenticated` — the pdf-generator's headless browser has
 *    no session; auth is via the short-lived `?token=`.
 *  - Fetches the token-gated `/v1/event-records/:id/print` endpoint.
 *  - Adds `body.pdf-ready` once rendered so the generator knows it can print.
 *  - Renders the structured `data` over `eventTypeFieldSchema.fields` in order.
 */

type PrintSearch = { token: string };

type PrintError =
  | { kind: "missing-token" }
  | { kind: "expired-or-invalid"; status: number }
  | { kind: "not-found"; status: number }
  | { kind: "unexpected"; status?: number };

class PrintFetchError extends Error {
  constructor(
    public readonly kind: "expired-or-invalid" | "not-found" | "unexpected",
    public readonly status: number,
  ) {
    super(`Print fetch failed: ${status}`);
  }
}

function titleFor(err: PrintError): string {
  switch (err.kind) {
    case "missing-token":
      return "Print link is missing its token";
    case "expired-or-invalid":
      return "This print link has expired";
    case "not-found":
      return "Event record not found";
    case "unexpected":
      return "We couldn't load this record";
  }
}

function messageFor(err: PrintError): string {
  switch (err.kind) {
    case "missing-token":
      return "Print links must be generated from the event record page in Naud. Open the record and click “Download PDF” to get a fresh link.";
    case "expired-or-invalid":
      return "Print links are short-lived for security. Open the record in Naud and click “Download PDF” again to generate a new one.";
    case "not-found":
      return "The record in this link no longer exists.";
    case "unexpected":
      return err.status
        ? `Something went wrong while loading this record (status ${err.status}). Please try generating the PDF again.`
        : "Something went wrong while loading this record. Please try generating the PDF again.";
  }
}

/**
 * Compact, machine-readable reason for the headless PDF generator. The
 * service reads this off `body[data-pdf-error]` so a failed print surfaces
 * as a fast, diagnosable error instead of an opaque `.pdf-ready` timeout.
 */
function pdfErrorReason(err: PrintError): string {
  return "status" in err && err.status ? `${err.kind}:${err.status}` : err.kind;
}

export const Route = createFileRoute("/print/event-records/$eventRecordId")({
  validateSearch: (search: Record<string, unknown>): PrintSearch => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: EventRecordPrintPage,
});

function EventRecordPrintPage() {
  const { eventRecordId } = useParams({
    from: "/print/event-records/$eventRecordId",
  });
  const { token } = useSearch({ from: "/print/event-records/$eventRecordId" });

  const [data, setData] = useState<EventRecordDetailResponseDto | null>(null);
  const [error, setError] = useState<PrintError | null>(null);

  useEffect(() => {
    if (!token) {
      setError({ kind: "missing-token" });
      return;
    }

    const url = `${config.apiUrl}/v1/event-records/${encodeURIComponent(eventRecordId)}/print?token=${encodeURIComponent(token)}`;

    fetch(url, { headers: { Accept: "application/json" } })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 401) {
            throw new PrintFetchError("expired-or-invalid", res.status);
          }
          if (res.status === 404) {
            throw new PrintFetchError("not-found", res.status);
          }
          throw new PrintFetchError("unexpected", res.status);
        }
        return res.json();
      })
      .then((body: EventRecordDetailResponseDto) => setData(body))
      .catch((err: unknown) => {
        if (err instanceof PrintFetchError) {
          setError({ kind: err.kind, status: err.status });
        } else {
          setError({ kind: "unexpected" });
        }
      });
  }, [eventRecordId, token]);

  useEffect(() => {
    if (data) document.body.classList.add("pdf-ready");
    return () => {
      document.body.classList.remove("pdf-ready");
    };
  }, [data]);

  // On failure, signal the headless browser so the PdfService can fail fast
  // with a diagnosable reason instead of waiting out the full selector
  // timeout. Mirrors the `.pdf-ready` convention.
  useEffect(() => {
    if (error) {
      document.body.dataset.pdfError = pdfErrorReason(error);
      document.body.classList.add("pdf-error");
    }
    return () => {
      document.body.classList.remove("pdf-error");
      delete document.body.dataset.pdfError;
    };
  }, [error]);

  if (error) {
    return (
      <div className="p-8 font-sans text-black max-w-[640px] mx-auto">
        <h1 className="text-xl font-semibold mb-3">{titleFor(error)}</h1>
        <p className="text-neutral-700">{messageFor(error)}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 font-sans text-black">
        <p>Loading…</p>
      </div>
    );
  }

  return <EventRecordPrintLayout record={data} />;
}

function EventRecordPrintLayout({
  record,
}: {
  record: EventRecordDetailResponseDto;
}) {
  const schema = parseFieldSchema(record.eventTypeFieldSchema);
  const fieldData = record.data as Record<string, unknown>;
  const derivedRows = deriveTimingRows(schema, fieldData);
  const submittedAt = format(
    new Date(record.submittedAt),
    "d MMMM yyyy, HH:mm",
  );
  const generatedAt = format(new Date(), "d MMMM yyyy, HH:mm");
  const submitter = record.createdBy ? userLabel(record.createdBy) : "—";
  const acknowledgement = record.acknowledgement ?? null;
  // The record's lifecycle state, so the sheet agrees with the app (NAU-342).
  const printStatus = eventRecordPrintStatus(record.status);

  return (
    <div className="event-record-print bg-white text-black">
      <style>{PRINT_STYLES}</style>

      <main className="sheet">
        <header className="doc-header">
          <div className="doc-header-row">
            <div>
              <h1 className="doc-title">{record.eventTypeName}</h1>
              <p className="doc-subtitle">Event record</p>
            </div>
            <div className="doc-status-block">
              <span className={`status-pill ${printStatus.tone}`}>
                {printStatus.label}
              </span>
              <div className="doc-id">
                Record ID:&nbsp;
                <span className="mono">{record.id.slice(0, 8)}</span>
              </div>
            </div>
          </div>
          <hr className="doc-rule" />
        </header>

        <section className="summary-section">
          <div className="summary-grid">
            <Field label="Submitted" value={submittedAt} />
            <Field
              label="Submitted by"
              value={submitter}
              muted={!record.createdBy}
            />
          </div>
        </section>

        <section className="fields-section">
          <h2 className="section-heading">Record details</h2>
          <div className="fields-grid">
            {schema.fields.map((field) => (
              <Field
                key={field.key}
                label={field.label}
                value={formatFieldValue(field, fieldData[field.key])}
              />
            ))}
            {/* Calculated, display-only timings (never stored). */}
            {derivedRows.map((row) => (
              <Field key={row.key} label={row.label} value={row.value ?? "—"} />
            ))}
          </div>
        </section>

        {acknowledgement && (
          <section className="callout callout-success avoid-break">
            <div className="callout-label">Acknowledgement</div>
            <div className="callout-body">
              <span className="callout-meta">
                Acknowledged by{" "}
                {acknowledgement.acknowledgedBy
                  ? userLabel(acknowledgement.acknowledgedBy)
                  : "a manager"}{" "}
                ·{" "}
                {format(
                  new Date(acknowledgement.acknowledgedAt),
                  "d MMMM yyyy, HH:mm",
                )}
              </span>
            </div>
          </section>
        )}

        {/* Corrective follow-up tasks (NAU-221). Omitted entirely when none
            exist so a printed empty block isn't noise. */}
        {record.followUpActions.length > 0 && (
          <section className="followups-section">
            <h2 className="section-heading">Corrective follow-up tasks</h2>
            <ol className="followups-list">
              {record.followUpActions.map((task) => (
                <FollowUpTaskItem key={task.id} task={task} />
              ))}
            </ol>
          </section>
        )}

        <footer className="doc-footer">
          <span>Generated {generatedAt}</span>
          {record.followUpActions.length > 0 && (
            <span>
              {record.followUpActions.length} follow-up task
              {record.followUpActions.length === 1 ? "" : "s"}
            </span>
          )}
        </footer>
      </main>
    </div>
  );
}

function FollowUpTaskItem({
  task,
}: {
  task: EventRecordDetailResponseDto["followUpActions"][number];
}) {
  const statusLabel: Record<typeof task.status, string> = {
    OPEN: "Open",
    OVERDUE: "Overdue",
    WAITING_APPROVAL: "Waiting for sign-off",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
  };
  const statusClass: Record<typeof task.status, string> = {
    OPEN: "pill-neutral",
    OVERDUE: "pill-error",
    WAITING_APPROVAL: "pill-brand",
    COMPLETED: "pill-success",
    CANCELLED: "pill-muted",
  };
  const assigneeLabel = task.assigneeUser
    ? userLabel(task.assigneeUser)
    : task.assigneeRole
      ? humanRole(task.assigneeRole)
      : "Unassigned";
  const dueLabel = task.dueDate
    ? task.dueTime
      ? `${format(new Date(task.dueDate), "d MMM yyyy")}, ${task.dueTime}`
      : format(new Date(task.dueDate), "d MMM yyyy")
    : "No due date";
  const createdLabel = format(new Date(task.createdAt), "d MMM yyyy, HH:mm");

  return (
    <li className="followup-item">
      <div className="followup-row">
        <span className="followup-description">{task.description}</span>
        <span className={`row-pill ${statusClass[task.status]}`}>
          {statusLabel[task.status]}
        </span>
      </div>
      <div className="followup-meta">
        <span>
          <span className="meta-label">Assigned to:</span> {assigneeLabel}
        </span>
        <span>
          <span className="meta-label">Due:</span> {dueLabel}
        </span>
        <span>
          <span className="meta-label">Created:</span> {createdLabel}
        </span>
      </div>
      {task.completedAt && (
        <div className="followup-resolution">
          <div className="followup-meta">
            <span>
              <span className="meta-label">Completed:</span>{" "}
              {format(new Date(task.completedAt), "d MMM yyyy, HH:mm")}
            </span>
            {task.completedBy && (
              <span>
                <span className="meta-label">By:</span>{" "}
                {userLabel(task.completedBy)}
              </span>
            )}
          </div>
          {task.completionNotes && (
            <p className="followup-notes">{task.completionNotes}</p>
          )}
        </div>
      )}
    </li>
  );
}

function humanRole(role: string): string {
  return role
    .split("_")
    .map((p) => p[0] + p.slice(1).toLowerCase())
    .join(" ");
}

function Field({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className={`field-value ${muted ? "muted" : ""}`}>{value}</span>
    </div>
  );
}

function userLabel(u: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email;
}

const PRINT_STYLES = `
  @page {
    size: A4;
    margin: 18mm 16mm 22mm 16mm;
  }
  @media print {
    html, body {
      background: white !important;
      color: black !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
  .event-record-print {
    color-scheme: light;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    color: #111;
  }
  .event-record-print .sheet {
    max-width: 178mm;
    margin: 0 auto;
    padding: 4mm;
    font-size: 10.5pt;
    line-height: 1.4;
  }

  .doc-header { margin-bottom: 6mm; }
  .doc-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12mm;
  }
  .doc-title { font-size: 18pt; font-weight: 600; margin: 0 0 1mm 0; letter-spacing: -0.01em; }
  .doc-subtitle { font-size: 13pt; color: #444; margin: 0; }
  .doc-status-block { text-align: right; }
  .doc-id { margin-top: 1.5mm; font-size: 8.5pt; color: #555; }
  .doc-rule { border: 0; border-top: 0.5pt solid #000; margin: 3mm 0 0 0; }

  .mono { font-family: "SF Mono", Menlo, Consolas, monospace; }

  .status-pill {
    display: inline-block;
    padding: 1mm 3mm;
    font-size: 9pt;
    font-weight: 600;
    border-radius: 2mm;
    border: 0.5pt solid;
  }
  .status-pill.pending { background: #fff7ed; border-color: #fb923c; color: #9a3412; }
  .status-pill.ack { background: #ecfdf5; border-color: #34d399; color: #065f46; }
  .status-pill.progress { background: #fffbeb; border-color: #fbbf24; color: #92400e; }
  .status-pill.archived { background: #f8fafc; border-color: #94a3b8; color: #334155; }

  .summary-section { margin-bottom: 6mm; }
  .summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3mm 8mm;
  }

  .section-heading {
    font-size: 12pt;
    font-weight: 600;
    margin: 0 0 2mm 0;
    padding-bottom: 1mm;
    border-bottom: 0.5pt solid #000;
  }
  .fields-section { margin-bottom: 6mm; }
  .fields-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3mm 8mm;
  }

  .field { display: flex; flex-direction: column; gap: 0.6mm; break-inside: avoid; }
  .field-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.05em; color: #666; }
  .field-value { font-size: 10.5pt; font-weight: 500; white-space: pre-wrap; }
  .field-value.muted { color: #999; }

  .callout {
    border: 0.5pt solid;
    border-radius: 2mm;
    padding: 3mm 4mm;
    margin-bottom: 5mm;
  }
  .callout.avoid-break { break-inside: avoid; page-break-inside: avoid; }
  .callout-success { background: #ecfdf5; border-color: #34d399; }
  .callout-label {
    font-size: 8pt;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #555;
    margin-bottom: 1.5mm;
  }
  .callout-body { display: flex; flex-direction: column; gap: 1.5mm; }
  .callout-meta { font-size: 9.5pt; color: #333; }

  .doc-footer {
    margin-top: 6mm;
    padding-top: 2mm;
    border-top: 0.5pt solid #000;
    display: flex;
    justify-content: space-between;
    font-size: 8.5pt;
    color: #555;
  }

  .followups-section { margin-bottom: 6mm; }
  .followups-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 3mm;
  }
  .followup-item {
    break-inside: avoid;
    page-break-inside: avoid;
    border: 0.5pt solid #d4d4d8;
    border-radius: 1.5mm;
    padding: 2.5mm 3mm;
  }
  .followup-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 4mm;
    margin-bottom: 1.5mm;
  }
  .followup-description {
    flex: 1;
    font-size: 10.5pt;
    font-weight: 500;
    color: #111;
  }
  .followup-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5mm 5mm;
    font-size: 9pt;
    color: #444;
  }
  .followup-meta .meta-label {
    color: #888;
    font-weight: 500;
  }
  .followup-resolution {
    margin-top: 2mm;
    padding-top: 2mm;
    border-top: 0.25pt dashed #d4d4d8;
  }
  .followup-notes {
    margin: 1.5mm 0 0 0;
    font-size: 9.5pt;
    color: #222;
    white-space: pre-wrap;
  }
  .row-pill {
    display: inline-block;
    padding: 0.5mm 2mm;
    font-size: 8pt;
    font-weight: 600;
    border-radius: 1.5mm;
    border: 0.5pt solid;
    white-space: nowrap;
  }
  .row-pill.pill-neutral { background: #f4f4f5; border-color: #a1a1aa; color: #3f3f46; }
  .row-pill.pill-error { background: #fee2e2; border-color: #ef4444; color: #991b1b; }
  .row-pill.pill-brand { background: #ede9fe; border-color: #8b5cf6; color: #4c1d95; }
  .row-pill.pill-success { background: #ecfdf5; border-color: #34d399; color: #065f46; }
  .row-pill.pill-muted { background: #f4f4f5; border-color: #d4d4d8; color: #71717a; }
`;
